import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReportStateContext } from '../../context/ReportStateContext';
import { ReportStep } from '../../hooks/report/types';
import { ReportProgress, ReportItemData } from '../../types/reportProgress';
import { reportStepsAPI, reportAPI } from '../../services/api';
import { getTaskSelection } from '../../utils/taskIdValidator';
import { refreshAccessTokenForSSE } from '../../utils/sseTokenRefresh';
import { SseReconnectPolicy } from '../../utils/sseReconnect';
import ReportCardsList from './ReportCardsList';
import ReportResults from './ReportResults';
import CompactHeader from './CompactHeader';
import wordExporter from '../../utils/wordExporter';
import pdfExporter from '../../utils/pdfExporter';
import PartialSummaryDisplay from './PartialSummaryDisplay';
import { PartialResultRenderer, PartialResult } from './display/PartialResultRenderer';
import './ReportSteps.css';
import '../../styles/safeDomErrorStyles.css';

// CSS styles for the Process All Reports button
const processAllButtonStyles = `
.process-all-container {
  display: flex;
  justify-content: center;
  margin: 20px 0;
  padding: 0 20px;
}

.process-all-button {
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.process-all-button:hover {
  background-color: #3a7bc8;
}

.process-all-button:active {
  background-color: #2a6cb8;
  transform: translateY(1px);
}
`;

/**
 * ProcessingStep component - simplified version that only displays report status and results
 * All processing is done on the backend
 */
const ProcessingStep: React.FC = () => {
  const { t } = useTranslation('create');
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { state: { progress: ctxProgress, progressItems: ctxProgressItems }, setProgress, setProgressItems, updateProgressItem } = useReportStateContext();

  // State for reports and results
  const [reports, setReports] = useState<ReportStep[]>([]);
  const [reportResults, setReportResults] = useState<{[key: number]: any}>({});
  const [partialResults, setPartialResults] = useState<{[key: number]: {name: string; content: PartialResult; isFinal: boolean}}>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [isSummaryGenerating, setIsSummaryGenerating] = useState<boolean>(false);
  const [summaryPartialResult, setSummaryPartialResult] = useState<{content: any; isFinal: boolean} | null>(null);
  const [isPreprocessing, setIsPreprocessing] = useState<boolean>(false);
  const [proModeQueries, setProModeQueries] = useState<{[reportIndex: number]: Array<{iteration: number; query: string; purpose: string; row_count: number; execution_time_ms: number; columns?: string[]; rows?: any[][]; preview_truncated?: boolean}>}>({});
  const [isProMode, setIsProMode] = useState<boolean>(false);
  const [isCollectingData, setIsCollectingData] = useState<boolean>(false);
  
  // Ref for tracking the status update interval and SSE connection
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseConnectedRef = useRef<boolean>(false);
  const initialized = useRef<boolean>(false);
  
  // Check authentication
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);
  
  // Get taskId from localStorage
  const { taskId } = getTaskSelection();
  
  // Function to load task status with reports and results
  const loadTaskStatus = async () => {
    if (!taskId) {
      setError(t('processing.error_task_not_init'));
      setIsLoading(false);
      return;
    }
    
    try {
      console.log('ProcessingStep - Загрузка статуса задачи:', taskId);
      const response = await reportStepsAPI.getTaskStatusWithReports(taskId);
      
      // Update state with task status
      setTaskStatus(response.data);
      
      // Check if summary is being generated
      if (response.data.message && (response.data.message.includes('Генерация итогового саммари') || response.data.message.includes('Generating final summary'))) {
        console.log('ProcessingStep - Обнаружена генерация саммари');
        setIsSummaryGenerating(true);
      } else if (response.data.summary) {
        // If summary is available, it means generation is complete
        setIsSummaryGenerating(false);
      }
      
      // Update reports if available - but preserve processing status for reports with partial results
      if (response.data.reports && Array.isArray(response.data.reports)) {
        console.log('ProcessingStep - Получены отчеты от сервера:', response.data.reports);
        
        setReports(prevReports => {
          // Create a map of reports with partial results that are still processing
          const reportsWithPartialResultsMap = Object.keys(partialResults)
            .filter(index => {
              const reportIdx = Number(index);
              const report = prevReports.find(r => r.index === reportIdx);
              return report && report.status === 'processing' && !partialResults[reportIdx].isFinal;
            })
            .reduce((map, index) => {
              map[Number(index)] = true;
              return map;
            }, {} as {[key: number]: boolean});
          
          // Update reports but preserve processing status for reports with partial results
          return response.data.reports.map((report: ReportStep) => {
            // If this report has partial results and is currently processing, keep it as processing
            if (reportsWithPartialResultsMap[report.index]) {
              console.log(`ProcessingStep - Preserving processing status for report ${report.index} with partial results`);
              return {
                ...report,
                status: 'processing'
              };
            }
            return report;
          });
        });
      }
      
      // Update report results if available, but preserve existing results
      if (response.data.report_results) {
        setReportResults(prevResults => {
          const newResults = { ...prevResults };
          
          // Only update results for reports that don't already have data
          Object.entries(response.data.report_results).forEach(([index, result]) => {
            const reportIndex = Number(index);
            
            // If we don't have this report result yet, or if it was an empty placeholder, update it
            if (!newResults[reportIndex] || (newResults[reportIndex] && newResults[reportIndex].empty)) {
              console.log(`ProcessingStep - Добавление данных для отчета ${reportIndex} из task status`);
              newResults[reportIndex] = result;
            } else {
              console.log(`ProcessingStep - Сохранение существующих данных для отчета ${reportIndex}`);
            }
          });
          
          return newResults;
        });
      }
      
      // Update summary if available
      if (response.data.summary) {
        setSummary(response.data.summary);
      }
      
      // If task is completed, clear interval
      // Also stop polling when report_id exists and summary is available (task status may stay "initialized" in DB)
      if (response.data.status === 'success' || response.data.status === 'error' ||
          (response.data.report_id && response.data.summary)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Ошибка при загрузке статуса задачи:', error);
      setError(t('processing.error_load_status'));
      setIsLoading(false);
    }
  };
  
  // Helper: apply progress snapshot from SSE events or API responses
  const applyProgressSnapshot = (data: any) => {
    if (data.progress) {
      setProgress(data.progress as ReportProgress);
    }
    if (data.items) {
      setProgressItems(data.items as ReportItemData[]);
    }
    if (data.item) {
      updateProgressItem(data.item as ReportItemData);
    }
  };

  // Fetch progress from the dedicated progress endpoint
  const fetchProgress = async () => {
    if (!taskId) return;
    try {
      const response = await reportStepsAPI.getTaskProgress(taskId);
      if (response.data) {
        applyProgressSnapshot(response.data);
      }
    } catch (err) {
      console.log('ProcessingStep - Progress endpoint not available, using legacy polling');
    }
  };

  // Initialize component — load status once on mount (polling managed by SSE state)
  useEffect(() => {
    console.log('ProcessingStep - Initializing for task:', taskId);
    loadTaskStatus();
    fetchProgress();
    // No interval here — fallback polling starts/stops via SSE onopen/onerror

    // Clean up interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Stop polling when task is completed or when we already have the summary
  useEffect(() => {
    if (taskStatus?.step === 'completed' || !!summary) {
      console.log('ProcessingStep - Task completed or summary available, stopping polling');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [taskStatus?.step, summary]);
  
  // Generate a client ID for SSE connection
  const generateClientId = () => {
    // Try to get the user ID from the token
    let userId = "anonymous";
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        // JWT tokens are in the format header.payload.signature
        // We need to decode the payload (second part)
        const payload = token.split('.')[1];
        // Decode the base64 payload
        const decodedPayload = JSON.parse(atob(payload));
        // Get the user ID from the sub claim
        if (decodedPayload.sub) {
          userId = decodedPayload.sub;
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
    
    return `user_${userId}_${Math.random().toString(36).substring(2, 9)}`;
  };
  
  // Get client ID from location state or localStorage, or generate a new one
  const location = useLocation();
  const getClientId = () => {
    // First try to get from location state
    if (location.state?.clientId) {
      console.log('ProcessingStep - Using client_id from location state:', location.state.clientId);
      return location.state.clientId;
    }
    
    // Then try to get from localStorage
    const storedClientId = localStorage.getItem('report_processing_client_id');
    if (storedClientId) {
      console.log('ProcessingStep - Using client_id from localStorage:', storedClientId);
      return storedClientId;
    }
    
    // Finally generate a new one
    const newClientId = generateClientId();
    console.log('ProcessingStep - Generated new client_id:', newClientId);
    return newClientId;
  };
  
  // Store the client ID in a ref to keep it consistent across renders
  const clientIdRef = useRef<string>(getClientId());
  
  // Set up SSE connection for real-time updates
  useEffect(() => {
    if (!taskId) return;

    // One-shot flag: attempt a single token refresh+reconnect when SSE
    // fails. Reset on successful open so a later expiration can be
    // recovered transparently. Without the flag, a refresh that itself
    // returns 401 would loop against the same dead token.
    let refreshAttempted = false;

    // Exponential-backoff reconnect policy: attempts counter only resets
    // after 10s of a stable (uninterrupted) connection. Without this, an
    // onopen that fires immediately (server returns 200 instantly) would
    // reset the counter every open→error cycle, producing a ~300ms
    // reconnect loop in production.
    const policy = new SseReconnectPolicy();
    // Re-arm the one-shot refresh flag only once the connection has proven
    // stable for 10s — so a later, unrelated token expiration can still
    // trigger a refresh, while a flapping connection doesn't re-open the
    // refresh gate on every spurious open.
    policy.onStable = () => { refreshAttempted = false; };
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconnect = () => {
      if (reconnectTimer) return; // не дублировать таймер
      reconnectTimer = setTimeout(() => { reconnectTimer = null; connectSSE(); },
                                  policy.nextDelayMs());
    };

    const connectSSE = () => {
      // Get the authentication token
      const token = localStorage.getItem('token');

      // Create SSE connection using the stored client ID and include the token as a query parameter
      const eventSource = new EventSource(`/api/v1/sse/connect/${clientIdRef.current}?token=${token}`);
      eventSourceRef.current = eventSource;

    console.log('ProcessingStep - Setting up SSE connection with client ID:', clientIdRef.current);

    // Handle connection open
    eventSource.onopen = () => {
      console.log('ProcessingStep - SSE connection opened');
      sseConnectedRef.current = true;
      // refreshAttempted is no longer reset here directly — policy.onOpen()
      // arms a 10s stable-timer that resets both the backoff counter and
      // (via onStable, wired above) refreshAttempted, but only if the
      // connection survives that long without another error.
      policy.onOpen();
      // Stop fallback polling — SSE is active
      if (intervalRef.current) {
        console.log('ProcessingStep - SSE connected, stopping fallback polling');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Handle connection error — try token refresh once, then fall back to polling.
    // EventSource doesn't expose the HTTP status (403 on expired token),
    // so refresh-and-reconnect is our blind first attempt before giving up.
    eventSource.onerror = async (error) => {
      console.error('ProcessingStep - SSE connection error:', error);
      sseConnectedRef.current = false;
      policy.onErrorOrClose();

      if (!refreshAttempted) {
        refreshAttempted = true;
        // Close this source so the browser doesn't keep auto-reconnecting
        // with the stale token while we refresh.
        eventSource.close();
        if (eventSourceRef.current === eventSource) {
          eventSourceRef.current = null;
        }

        const newToken = await refreshAccessTokenForSSE();
        if (newToken) {
          console.log('ProcessingStep - Token refreshed after SSE error, reconnecting');
          scheduleReconnect();
          return;
        }
        console.warn('ProcessingStep - Token refresh failed, falling back to polling');
      }

      // Start fallback polling if not already running
      if (!intervalRef.current) {
        console.log('ProcessingStep - SSE down, starting fallback polling');
        intervalRef.current = setInterval(() => {
          console.log('ProcessingStep - Fallback polling (SSE down):', taskId);
          loadTaskStatus();
        }, 20000);
      }
    };
    
    // Handle partial result events
    eventSource.addEventListener('report_partial_result', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received partial result:', data);
        
        const reportIndex = data.report_index;
        const reportName = data.report_name;
        const partialResult = data.partial_result;
        const isFinal = data.is_final;
        
        // Log the partial result content for debugging
        console.log('ProcessingStep - Partial result content:', JSON.stringify(partialResult).substring(0, 200) + '...');
        
        // Update partial results state
        setPartialResults(prev => {
          console.log('ProcessingStep - Updating partial results for report:', reportIndex);
          return {
            ...prev,
            [reportIndex]: {
              name: reportName,
              content: partialResult,
              isFinal: isFinal
            }
          };
        });
        
        // If this is the final result, update the report results
        if (isFinal) {
          console.log('ProcessingStep - Received final result for report:', reportIndex);
          setReportResults(prev => ({
            ...prev,
            [reportIndex]: partialResult
          }));
          
          // Update report status to success ONLY if it's the final result
          setReports(prev => 
            prev.map(report => 
              report.index === reportIndex 
                ? { ...report, status: 'success' } 
                : report
            )
          );
        } else {
          // Always ensure the report status is set to processing for partial results
          setReports(prev => {
            return prev.map(report => 
              report.index === reportIndex 
                ? { ...report, status: 'processing' } 
                : report
            );
          });
        }
      } catch (error) {
        console.error('ProcessingStep - Error parsing partial result:', error);
      }
    });
    
    // Handle report start events
    eventSource.addEventListener('report_start', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received report start event:', data);
        applyProgressSnapshot(data);

        // Update report status to processing
        if (data.report_index) {
          setReports(prev =>
            prev.map(report =>
              report.index === data.report_index
                ? { ...report, status: 'processing' }
                : report
            )
          );
        }
      } catch (error) {
        console.error('ProcessingStep - Error parsing report start event:', error);
      }
    });

    // Handle report complete events
    eventSource.addEventListener('report_complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received report complete event:', data);
        applyProgressSnapshot(data);

        // Phase 2 results arriving — collection phase is done
        setIsCollectingData(false);

        const reportIndex = data.report_index;
        if (reportIndex) {
          // Update report status to success
          setReports(prev =>
            prev.map(report =>
              report.index === reportIndex
                ? { ...report, status: 'success' }
                : report
            )
          );

          // Extract report data directly from SSE event if available
          // This avoids the race condition where getReportResult returns null
          // because the DB hasn't been updated yet
          if (data.data && typeof data.data === 'object' && !data.data.empty) {
            console.log(`ProcessingStep - Using report data from SSE event for report ${reportIndex}`);
            setReportResults(prev => ({
              ...prev,
              [reportIndex]: data.data
            }));
          }
        }
      } catch (error) {
        console.error('ProcessingStep - Error parsing report complete event:', error);
      }
    });

    // Handle report error events
    eventSource.addEventListener('report_error', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received report error event:', data);
        applyProgressSnapshot(data);

        // Update report status to error
        if (data.report_index) {
          setReports(prev =>
            prev.map(report =>
              report.index === data.report_index
                ? { ...report, status: 'error' }
                : report
            )
          );
        }
      } catch (error) {
        console.error('ProcessingStep - Error parsing report error event:', error);
      }
    });
    
    // Handle data preprocessing events
    eventSource.addEventListener('data_preprocessing_start', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Data preprocessing started:', data);
        setIsPreprocessing(true);
      } catch (error) {
        console.error('ProcessingStep - Error parsing preprocessing start event:', error);
      }
    });

    eventSource.addEventListener('data_preprocessing_complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Data preprocessing complete:', data);
        setIsPreprocessing(false);
      } catch (error) {
        console.error('ProcessingStep - Error parsing preprocessing complete event:', error);
      }
    });

    // Handle reports processing start events
    eventSource.addEventListener('reports_processing_start', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received reports processing start event:', data);
        applyProgressSnapshot(data);
      } catch (error) {
        console.error('ProcessingStep - Error parsing reports processing start event:', error);
      }
    });

    // Handle reports processing complete events
    eventSource.addEventListener('reports_processing_complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received reports processing complete event:', data);
        applyProgressSnapshot(data);

        // Reload task status to get the latest data
        loadTaskStatus();
      } catch (error) {
        console.error('ProcessingStep - Error parsing reports processing complete event:', error);
      }
    });

    // Handle reports_all_complete events (final completion with summary)
    eventSource.addEventListener('reports_all_complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received reports all complete event:', data);
        applyProgressSnapshot(data);

        // Update summary if provided
        if (data.summary) {
          setSummary(data.summary);
          setIsSummaryGenerating(false);
        }

        // Reload task status to get the final data
        loadTaskStatus();
        fetchProgress();
      } catch (error) {
        console.error('ProcessingStep - Error parsing reports all complete event:', error);
      }
    });

    // Handle task_progress_report_processing events (progress updates)
    eventSource.addEventListener('task_progress_report_processing', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received task progress event:', data);

        // Update progress if available
        if (data.progress?.progress) {
          const progress = data.progress.progress;
          console.log(`ProcessingStep - Progress: ${progress.current}/${progress.total} (${progress.percentage}%)`);
        }
      } catch (error) {
        console.error('ProcessingStep - Error parsing task progress event:', error);
      }
    });

    // Handle task_complete_report_processing events (task completion)
    eventSource.addEventListener('task_complete_report_processing', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received task complete event:', data);
        applyProgressSnapshot(data);

        // Reload task status to get final data
        loadTaskStatus();
        fetchProgress();
      } catch (error) {
        console.error('ProcessingStep - Error parsing task complete event:', error);
      }
    });

    // Handle summary partial result events
    eventSource.addEventListener('summary_partial_result', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Received summary partial result:', data);
        
        const partialResult = data.partial_result;
        const isFinal = data.is_final;
        
        // Log the partial result content for debugging
        console.log('ProcessingStep - Summary partial result content:', 
          JSON.stringify(partialResult).substring(0, 200) + '...');
        
        // Update summary partial result state
        setSummaryPartialResult({
          content: partialResult,
          isFinal: isFinal
        });
        
        // If this is the final result, update the summary
        if (isFinal) {
          console.log('ProcessingStep - Received final summary result');
          setSummary(partialResult);
          setIsSummaryGenerating(false);
        } else {
          // Ensure the summary generating state is true for partial results
          setIsSummaryGenerating(true);
        }
      } catch (error) {
        console.error('ProcessingStep - Error parsing summary partial result:', error);
      }
    });
    
    // Handle Pro Mode start events
    eventSource.addEventListener('pro_mode_start', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Pro mode started:', data);
        setIsProMode(true);

        // Two-phase pro mode: collection phase started for all cards
        if (data.phase === 'data_collection' && data.report_index === -1) {
          console.log('ProcessingStep - Data collection phase started, setting all cards to processing');
          setIsCollectingData(true);
          // Set ALL pending cards to "processing" so they show animation
          setReports(prev => prev.map(report =>
            report.status === 'pending' && !report.deleted
              ? { ...report, status: 'processing' }
              : report
          ));
        }
      } catch (error) {
        console.error('ProcessingStep - Error parsing pro_mode_start event:', error);
      }
    });

    // Handle Pro Mode Phase 2 start (generation from collected data)
    eventSource.addEventListener('pro_phase2_start', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Pro mode Phase 2 started (generation):', data);
        setIsCollectingData(false);
      } catch (error) {
        console.error('ProcessingStep - Error parsing pro_phase2_start event:', error);
      }
    });

    // Handle Pro Mode SQL query events
    eventSource.addEventListener('pro_mode_query', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - Pro mode query:', data);
        setIsProMode(true);
        const reportIndex = data.report_index;

        // If collection-phase query arrives, ensure isCollectingData + cards are processing
        if (reportIndex === -1 && data.phase === 'data_collection') {
          setIsCollectingData(true);
          setReports(prev => prev.map(report =>
            report.status === 'pending' && !report.deleted
              ? { ...report, status: 'processing' }
              : report
          ));
        }
        setProModeQueries(prev => ({
          ...prev,
          [reportIndex]: [
            ...(prev[reportIndex] || []),
            {
              iteration: data.iteration,
              query: data.query,
              purpose: data.purpose,
              row_count: data.row_count,
              execution_time_ms: data.execution_time_ms,
              columns: data.columns || [],
              rows: data.rows || [],
              preview_truncated: data.preview_truncated || false,
            }
          ]
        }));
      } catch (error) {
        console.error('ProcessingStep - Error parsing pro_mode_query event:', error);
      }
    });

    // Handle connection established events
    eventSource.addEventListener('connection_established', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('ProcessingStep - SSE connection established:', data);
      } catch (error) {
        console.error('ProcessingStep - Error parsing connection established event:', error);
      }
    });
    };

    connectSSE();

    // Clean up SSE connection on unmount
    return () => {
      console.log('ProcessingStep - Cleaning up SSE connection');
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      policy.onErrorOrClose();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [taskId]);
  
  // When isCollectingData becomes true (pro mode Phase 1) but reports were loaded after
  // the SSE event, ensure all pending cards are marked as "processing"
  useEffect(() => {
    if (isCollectingData && reports.length > 0) {
      const hasPending = reports.some(r => r.status === 'pending' && !r.deleted);
      if (hasPending) {
        console.log('ProcessingStep - isCollectingData active, switching pending cards to processing');
        setReports(prev => prev.map(report =>
          report.status === 'pending' && !report.deleted
            ? { ...report, status: 'processing' }
            : report
        ));
      }
    }
  }, [isCollectingData, reports.length]);

  // Check if all reports are processed
  const allReportsProcessed = React.useMemo(() => {
    if (!reports || reports.length === 0) {
      return false;
    }
    
    // Filter out deleted reports
    const nonDeletedReports = reports.filter(report => !report.deleted);
    
    // If there are no non-deleted reports, return false
    if (nonDeletedReports.length === 0) {
      return false;
    }
    
    // Check if we have at least one report with status success
    const hasSuccessReports = nonDeletedReports.some(report => report.status === 'success');
    
    // Check if all non-deleted reports are processed (success or error)
    const allProcessed = nonDeletedReports.every(report => 
      report.status === 'success' || report.status === 'error'
    );
    
    return hasSuccessReports && allProcessed;
  }, [reports]);
  
  // Automatically set summary generating state when all reports are processed but no summary yet
  // If report_id exists, first check if the report already has a summary (page refresh case)
  useEffect(() => {
    if (allReportsProcessed && !summary && taskId) {
      const checkSummaryState = async () => {
        // Step 1: If we have a report_id, try to get summary from the report
        if (taskStatus?.report_id) {
          try {
            console.log('ProcessingStep - Fetching report', taskStatus.report_id, 'to check for existing summary');
            const response = await reportAPI.getReport(taskStatus.report_id);
            if (response.data?.content?.summary) {
              console.log('ProcessingStep - Found existing summary in report, using it');
              setSummary(response.data.content.summary);
              setIsSummaryGenerating(false);
              return;
            }
            // Report exists but status is 'error' — pipeline failed
            if (response.data?.status === 'error') {
              console.log('ProcessingStep - Report status is error, not showing summary spinner');
              setIsSummaryGenerating(false);
              return;
            }
            // Check progress data from report (returned inline with report)
            const progress = response.data?.progress;
            if (progress) {
              const summaryStage = progress.summary;
              const reportsStage = progress.reports_processing;
              const hasError = progress.error;

              if (hasError) {
                console.log('ProcessingStep - Progress has error:', hasError, '- not showing summary spinner');
                setIsSummaryGenerating(false);
                return;
              }
              // Pipeline broke: reports completed but summary never started
              if (reportsStage?.status === 'completed' && summaryStage?.status === 'pending') {
                console.log('ProcessingStep - Reports completed but summary pending (pipeline broke) - not showing spinner');
                setIsSummaryGenerating(false);
                return;
              }
              // Summary is actively generating
              if (summaryStage?.status === 'in_progress') {
                console.log('ProcessingStep - Summary is in_progress, showing spinner');
                setIsSummaryGenerating(true);
                return;
              }
            }
          } catch (err) {
            console.log('ProcessingStep - Could not fetch report');
          }
        }

        // Step 2: Fallback — check progress endpoint
        try {
          console.log('ProcessingStep - Checking progress for task', taskId);
          const progressResponse = await reportStepsAPI.getTaskProgress(taskId);
          const progress = progressResponse.data?.progress;
          if (progress) {
            const summaryStage = progress.summary;
            const reportsStage = progress.reports_processing;
            const hasError = progress.error;

            if (hasError) {
              console.log('ProcessingStep - Progress has error:', hasError, '- not showing summary spinner');
              setIsSummaryGenerating(false);
              return;
            }
            if (reportsStage?.status === 'completed' && summaryStage?.status === 'pending') {
              console.log('ProcessingStep - Reports completed but summary pending (pipeline broke) - not showing spinner');
              setIsSummaryGenerating(false);
              return;
            }
            if (summaryStage?.status === 'in_progress') {
              console.log('ProcessingStep - Summary is in_progress, showing spinner');
              setIsSummaryGenerating(true);
              return;
            }
          }
        } catch (err) {
          console.log('ProcessingStep - Could not fetch progress');
        }

        // Default: assume summary is still generating (live processing scenario)
        console.log('ProcessingStep - Defaulting to isSummaryGenerating = true');
        setIsSummaryGenerating(true);
      };
      checkSummaryState();
    }
  }, [allReportsProcessed, summary, taskStatus?.report_id, taskId]);
  
  // Add the CSS styles to the document
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    styleElement.textContent = processAllButtonStyles;
    
    // Add the style element to the document head
    document.head.appendChild(styleElement);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Automatically load report data when a report's status changes to "success"
  useEffect(() => {
    if (!taskId) return;
    
    // Find reports with status "success" that don't have results yet
    const successReportsWithoutResults = reports.filter(report => 
      report.status === 'success' && 
      !report.deleted && 
      !reportResults[report.index]
    );
    
    // Load data for each report
    if (successReportsWithoutResults.length > 0) {
      console.log('ProcessingStep - Автоматическая загрузка данных для успешных отчетов:', 
        successReportsWithoutResults.map(r => r.index));
      
      // Load each report's data
      successReportsWithoutResults.forEach(async (report) => {
        try {
          console.log(`ProcessingStep - Автозагрузка данных отчета ${report.index}`);
          
          // Directly load the specific report result
          const response = await reportStepsAPI.getReportResult(taskId, report.index);
          console.log(`ProcessingStep - Автозагрузка: полный ответ API:`, response.data);
          
          // Handle different response formats from the backend
          if (response.data) {
            console.log(`ProcessingStep - Автозагрузка: полный ответ API для отчета ${report.index}:`, response.data);
            
            // Format 1: Response with reports array
            if (response.data.reports && response.data.reports.length > 0) {
              console.log(`ProcessingStep - Автозагрузка: получены данные отчета ${report.index} из массива reports:`, response.data.reports[0]);
              
              // Update report results with the new data
              setReportResults(prev => ({
                ...prev,
                [report.index]: response.data.reports[0].actions
              }));
            } 
            // Format 2: Direct response with report_status and report_result
            else if (response.data.report_status === 'success') {
              if (response.data.report_result) {
                console.log(`ProcessingStep - Автозагрузка: получены данные отчета ${report.index} из поля report_result:`, response.data.report_result);
                
                // Update report results with the new data
                setReportResults(prev => ({
                  ...prev,
                  [report.index]: response.data.report_result
                }));
              } else {
                console.log(`ProcessingStep - Автозагрузка: отчет ${report.index} имеет статус success, но данные отсутствуют. Создаем заглушку.`);
                
                // Create a placeholder for the report
                const placeholder = {
                  message: `"${response.data.report_name || `#${report.index}`}" — ${t('results.no_data')}`,
                  empty: true
                };
                
                // Update report results with the placeholder
                setReportResults(prev => ({
                  ...prev,
                  [report.index]: placeholder
                }));
              }
            } else {
              console.warn(`ProcessingStep - Автозагрузка: данные отчета ${report.index} не найдены в ответе API:`, response.data);
            }
          } else {
            console.warn(`ProcessingStep - Автозагрузка: пустой ответ API для отчета ${report.index}`);
          }
        } catch (error) {
          console.error(`Ошибка при автозагрузке отчета ${report.index}:`, error);
        }
      });
    }
  }, [reports, reportResults, taskId]);
  
  // Render loading state
  if (isLoading) {
    return (
      <div className="processing-step">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <p>{t('processing.loading')}</p>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="processing-step">
        <div className="error-container">
          <h3>{t('analytics_step.error_title')}</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>{t('processing.reload_page')}</button>
        </div>
      </div>
    );
  }
  
  // Determine if we should show the completion message
  const showCompletionMessage = allReportsProcessed && taskStatus?.report_id && !!summary;
  
  return (
    <div className="processing-step-wrapper">
      <CompactHeader reportTitle={summary?.report_title || t('processing.title')} />
      
      <div className="processing-step">
        {/* Data preprocessing indicator */}
        {isPreprocessing && (
          <div className="summary-generating-container">
            <div className="summary-generating-animation">
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
              <h3>{t('processing.preprocessing_title')}</h3>
              <p>{t('processing.preprocessing_desc')}</p>
            </div>
          </div>
        )}

        {/* Pro Mode SQL queries are now rendered inside each report card in ReportResults */}

        {/* Summary generation indicator with partial results */}
        {isSummaryGenerating && allReportsProcessed && !summary && (
          <div className="summary-generating-container">
            {summaryPartialResult ? (
              <PartialSummaryDisplay result={summaryPartialResult.content} />
            ) : (
              <div className="summary-generating-animation">
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
                <h3>{t('processing.summary_generating_title')}</h3>
                <p>{t('processing.summary_generating_desc')}</p>
                <div className="summary-animation">
                  <div className="summary-icon">📊</div>
                  <div className="summary-icon">📈</div>
                  <div className="summary-icon">📝</div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Floating left column */}
        <ReportCardsList
          reports={reports}
          progressItems={ctxProgressItems}
          onCardClick={(reportIndex) => {
            const element = document.getElementById(`report-${reportIndex}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        />
        
        {/* Process All Reports button - only when nothing is actively processing */}
        {(() => {
          const nonDeleted = reports.filter(r => !r.deleted);
          const hasProcessing = nonDeleted.some(r => r.status === 'processing');
          const hasPending = nonDeleted.some(r => r.status === 'pending');
          const hasError = nonDeleted.some(r => r.status === 'error');
          const showButton = !hasProcessing && (hasPending || hasError);
          const buttonLabel = hasError && !hasPending
            ? t('processing.retry_errors')
            : t('processing.process_all');

          return showButton ? (
            <div className="process-all-container">
              <button
                className="process-all-button"
                onClick={async () => {
                  try {
                    if (!taskId) {
                      console.error('ProcessingStep - taskId не найден');
                      return;
                    }

                    if (!clientIdRef.current) {
                      clientIdRef.current = generateClientId();
                    }

                    await reportStepsAPI.processAllReports(taskId, clientIdRef.current);
                    await loadTaskStatus();
                  } catch (error) {
                    console.error('Ошибка при запуске обработки отчетов:', error);
                  }
                }}
              >
                {buttonLabel}
              </button>
            </div>
          ) : null;
        })()}
        
        {/* Main content with left column padding */}
        <div className="main-content">
          <div className="results-container">
            {/* Debug info removed */}
            
            <ReportResults
              progress={ctxProgress}
              allReports={reports}
              reportResults={reportResults}
              loadingReportIndices={Object.keys(partialResults)
                .filter(index => !partialResults[Number(index)].isFinal)
                .map(Number)}
              reportsWithPartialResults={Object.keys(partialResults)
                .filter(index => {
                  const reportIdx = Number(index);
                  const report = reports.find(r => r.index === reportIdx);
                  const isProcessing = report && report.status === 'processing';
                  return isProcessing && !partialResults[reportIdx].isFinal;
                })
                .map(Number)}
              partialResults={partialResults}
              summaryPartialResult={summaryPartialResult}
              proModeQueries={proModeQueries}
              isProMode={isProMode}
              isCollectingData={isCollectingData}
              onReloadReport={async (reportIndex) => {
                try {
                  if (!taskId) {
                    console.error('ProcessingStep - taskId не найден, невозможно загрузить отчет');
                    return false;
                  }
                  
                  // Check if we already have data for this report and it's not an empty placeholder
                  if (reportResults[reportIndex] && !reportResults[reportIndex].empty) {
                    console.log(`ProcessingStep - Отчет ${reportIndex} уже загружен, пропускаем повторную загрузку`);
                    return true;
                  }
                  
                  console.log(`ProcessingStep - Загрузка данных отчета ${reportIndex}`);
                  
                  // Process the report with the client ID for streaming updates
                  console.log('ProcessingStep - Sending client_id for streaming updates:', clientIdRef.current);
                  await reportStepsAPI.processReport(taskId, reportIndex, clientIdRef.current);
                  
                  // Directly load the specific report result
                  const response = await reportStepsAPI.getReportResult(taskId, reportIndex);
                  console.log(`ProcessingStep - Полный ответ API:`, response.data);
                  
                  // Handle different response formats from the backend
                  if (response.data) {
                    console.log(`ProcessingStep - Полный ответ API для отчета ${reportIndex}:`, response.data);
                    
                    // Format 1: Response with reports array
                    if (response.data.reports && response.data.reports.length > 0) {
                      console.log(`ProcessingStep - Получены данные отчета ${reportIndex} из массива reports:`, response.data.reports[0]);
                      
                      // Update report results with the new data
                      setReportResults(prev => ({
                        ...prev,
                        [reportIndex]: response.data.reports[0].actions
                      }));
                      
                      return true;
                    } 
                    // Format 2: Direct response with report_status and report_result
                    else if (response.data.report_status === 'success') {
                      if (response.data.report_result) {
                        console.log(`ProcessingStep - Получены данные отчета ${reportIndex} из поля report_result:`, response.data.report_result);
                        
                        // Update report results with the new data
                        setReportResults(prev => ({
                          ...prev,
                          [reportIndex]: response.data.report_result
                        }));
                        
                        return true;
                      } else {
                        console.log(`ProcessingStep - Отчет ${reportIndex} имеет статус success, но данные отсутствуют. Создаем заглушку.`);
                        
                        // Create a placeholder for the report
                        const placeholder = {
                          message: `"${response.data.report_name || `#${reportIndex}`}" — ${t('results.no_data')}`,
                          empty: true
                        };
                        
                        // Update report results with the placeholder
                        setReportResults(prev => ({
                          ...prev,
                          [reportIndex]: placeholder
                        }));
                        
                        return true;
                      }
                    } else {
                      console.warn(`ProcessingStep - Данные отчета ${reportIndex} не найдены в ответе API:`, response.data);
                      return false;
                    }
                  } else {
                    console.warn(`ProcessingStep - Пустой ответ API для отчета ${reportIndex}`);
                    return false;
                  }
                } catch (error) {
                  console.error(`Ошибка при загрузке отчета ${reportIndex}:`, error);
                  return false;
                }
              }}
              summary={summary}
            />
          </div>
        </div>
      
        {showCompletionMessage && (
          <div className="completion-actions">
            <h3>{t('processing.completion_title')}</h3>
            <p>{t('processing.completion_desc')}</p>
            <div className="action-buttons">
              <button 
                className="primary-button" 
                onClick={() => navigate(`/reports/${taskStatus?.report_id}`)}
                disabled={!taskStatus?.report_id}
              >
                {t('processing.view_result')}
              </button>
              <button 
                className="secondary-button" 
                onClick={() => navigate(`/create-report`)}
              >
                {t('processing.create_new')}
              </button>
              {summary && (
                <>
                  <button
                    className="export-button"
                    onClick={() => {
                      const reportData = {
                        reports: reportResults ? Object.values(reportResults) : [],
                        summary: summary
                      };
                      wordExporter.exportToWord(reportData);
                    }}
                  >
                    <i className="fas fa-file-word"></i> Word
                  </button>
                  <button
                    className="export-button export-button-pdf"
                    onClick={() => pdfExporter.exportToPdf()}
                  >
                    <i className="fas fa-file-pdf"></i> PDF
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingStep;
